import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { ORBIS_PRODUCTS, SECTION_IDS, SIGN_IN_URL } from '../lib/links';
import { BLOG_PATH } from '../lib/blog';
import navLogo from '../assets/v2-nav-logo.png';
import microsoftLogo from '../assets/microsoft-logo.png';
import arrowDown from '../assets/v2-arrow-down.svg';

/**
 * Top navigation — Figma node 2:533.
 *
 * The bar is `max-w-[1782px]` with `xl:px-16` (1654 content + 2×64) so the logo
 * lands at x=133 at 1920 as designed, without the padding collapsing to zero
 * between 1536 and 1654 the way `2xl:px-0` did.
 *
 * Desktop (1920×96): logo · Platform / Solutions ▾ / Resources · search ·
 * "Sign in with Microsoft". Values: bg #121212, border-b rgba(186,186,186,0.5),
 * px-133 py-12; links Inter 400 20/24 white with gap-40; button 1.5px white
 * border, h-48, r-8, p-16, gap-8, Inter 600 16px.
 *
 * The 2026-07 refresh replaced the alpha-masked wordmark with a single logo
 * image (node 2:534, 316×82). Four `Elements/Navigation/Link` nodes are
 * `hidden` in the file, including a "Get started" — so they are not rendered.
 *
 * The "Solutions" dropdown lists the four Orbis products as plain text, not the
 * nine report sections as anchors — client instruction, and the product pages do
 * not exist yet. See ORBIS_PRODUCTS.
 *
 * Links are written as `/#anchor` rather than `#anchor` so they also work from
 * the legal, qualifier and blog pages; on the home page the browser treats them
 * as same-document jumps, with no reload.
 *
 * Two deliberate deviations, both agreed:
 * 1. The search icon is omitted. This is a static single page with nothing to
 *    search, so a live-looking magnifier would be a dead control.
 * 2. Below `lg` the links collapse into a drawer, because the file has no
 *    mobile frame. The desktop bar is reproduced exactly at `lg` and up.
 */

function MicrosoftSignInButton({ className }: { className?: string }) {
  return (
    <a
      href={SIGN_IN_URL}
      className={cn(
        'flex h-12 items-center justify-center gap-2 rounded-lg border-[1.5px] border-white px-4',
        'text-base font-semibold text-white transition-colors',
        'hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-300',
        className,
      )}
    >
      <img src={microsoftLogo} alt="" width={24} height={24} className="size-6 shrink-0" />
      <span className="whitespace-nowrap">Sign in with Microsoft</span>
    </a>
  );
}

export function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const solutionsRef = useRef<HTMLDivElement>(null);

  // Close the desktop dropdown on outside click and on Escape.
  useEffect(() => {
    if (!solutionsOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (!solutionsRef.current?.contains(event.target as Node)) setSolutionsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSolutionsOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [solutionsOpen]);

  // Prevent background scroll while the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  const onBlog = window.location.pathname.startsWith('/blog');

  const linkClass =
    'text-xl font-normal leading-6 text-white transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-300';

  return (
    <header className="sticky top-0 z-50 border-b border-landing-hairline bg-landing-ink">
      <div className="mx-auto flex h-16 max-w-[1782px] items-center justify-between px-5 sm:px-6 md:px-8 lg:h-24 lg:px-10 xl:px-16">
        {/* Logo — 316×82 at the designed 1920 width, scaled down below lg. */}
        <a href="/#top" className="shrink-0 no-underline" aria-label="WisdomX Orbis — home">
          <img
            src={navLogo}
            alt="WisdomX Orbis"
            width={316}
            height={82}
            className="block h-10 w-auto sm:h-12 lg:h-[82px]"
          />
        </a>

        {/* Desktop links — exact Figma gap-40 / 20px Inter. */}
        <nav className="hidden items-center gap-10 lg:flex" aria-label="Main">
          <a href={`/#${SECTION_IDS.platform}`} className={linkClass}>
            Platform
          </a>

          <div ref={solutionsRef} className="relative">
            <button
              type="button"
              onClick={() => setSolutionsOpen((open) => !open)}
              aria-expanded={solutionsOpen}
              aria-haspopup="true"
              className={cn(linkClass, 'flex items-center gap-2.5')}
            >
              Solutions
              <img
                src={arrowDown}
                alt=""
                width={16}
                height={16}
                className={cn('size-4 transition-transform', solutionsOpen && 'rotate-180')}
              />
            </button>

            {/* Product names only, no links — see ORBIS_PRODUCTS. A <ul> of
                plain items rather than anchors, so nothing looks clickable. */}
            {solutionsOpen && (
              <ul className="absolute left-0 top-full z-50 mt-3 w-[300px] list-none overflow-hidden rounded-lg border border-landing-hairline bg-landing-ink py-2 shadow-2xl">
                {ORBIS_PRODUCTS.map((item) => (
                  <li key={item} className="px-5 py-2.5 text-base text-white/90">
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <a href={`/#${SECTION_IDS.marketplace}`} className={linkClass}>
            Resources
          </a>

          <a href={BLOG_PATH} className={linkClass} aria-current={onBlog ? 'page' : undefined}>
            Blog
          </a>
        </nav>

        <div className="flex items-center gap-4">
          <MicrosoftSignInButton className="hidden lg:flex" />

          {/* Hamburger — below lg only. */}
          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            aria-expanded={mobileOpen}
            aria-controls="landing-mobile-nav"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            className="flex size-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/10 lg:hidden"
          >
            <span className="relative block h-4 w-6" aria-hidden="true">
              <span
                className={cn(
                  'absolute left-0 block h-0.5 w-6 bg-white transition-transform duration-200',
                  mobileOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0',
                )}
              />
              <span
                className={cn(
                  'absolute left-0 top-1/2 block h-0.5 w-6 -translate-y-1/2 bg-white transition-opacity duration-200',
                  mobileOpen && 'opacity-0',
                )}
              />
              <span
                className={cn(
                  'absolute left-0 block h-0.5 w-6 bg-white transition-transform duration-200',
                  mobileOpen ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0',
                )}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          id="landing-mobile-nav"
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-landing-hairline bg-landing-ink px-5 pb-6 pt-2 sm:px-6 md:px-8 lg:hidden"
        >
          <nav className="flex flex-col" aria-label="Mobile">
            <a
              href={`/#${SECTION_IDS.platform}`}
              onClick={() => setMobileOpen(false)}
              className="border-b border-white/10 py-4 text-lg text-white"
            >
              Platform
            </a>

            <button
              type="button"
              onClick={() => setSolutionsOpen((open) => !open)}
              aria-expanded={solutionsOpen}
              className="flex items-center justify-between border-b border-white/10 py-4 text-left text-lg text-white"
            >
              Solutions
              <img
                src={arrowDown}
                alt=""
                width={16}
                height={16}
                className={cn('size-4 transition-transform', solutionsOpen && 'rotate-180')}
              />
            </button>
            {solutionsOpen && (
              <ul className="flex list-none flex-col border-b border-white/10 bg-white/5">
                {ORBIS_PRODUCTS.map((item) => (
                  <li key={item} className="py-3 pl-4 text-base text-white/80">
                    {item}
                  </li>
                ))}
              </ul>
            )}

            <a
              href={`/#${SECTION_IDS.marketplace}`}
              onClick={() => setMobileOpen(false)}
              className="border-b border-white/10 py-4 text-lg text-white"
            >
              Resources
            </a>

            <a
              href={BLOG_PATH}
              aria-current={onBlog ? 'page' : undefined}
              className="border-b border-white/10 py-4 text-lg text-white"
            >
              Blog
            </a>
          </nav>

          <MicrosoftSignInButton className="mt-6 w-full" />
        </div>
      )}
    </header>
  );
}
